import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import { SERVICES, LOCATIONS, ABOUT, FAQ_PAGE } from "@/data/seoPages";

const SIMPLE_DUAL = ["/book", "/priority", "/membership", "/track", "/legal", "/sample-report"];

// Resolve the sibling-language URL for the current path (keeps each language on its own URL).
function siblingPath(pathname, target) {
  const en = target === "en";
  if (pathname === "/" || pathname === "/en") return en ? "/en" : "/";
  if (pathname === "/aviacion" || pathname === "/en/aviation") return en ? "/en/aviation" : "/aviacion";
  if (pathname === "/autos" || pathname === "/en/cars") return en ? "/en/cars" : "/autos";
  if (pathname === "/autos/cotizar" || pathname === "/en/cars/quote") return en ? "/en/cars/quote" : "/autos/cotizar";
  if (pathname === "/autos/membresias" || pathname === "/en/cars/memberships") return en ? "/en/cars/memberships" : "/autos/membresias";
  for (const s of SERVICES) {
    if (pathname === `/servicios/${s.esSlug}` || pathname === `/en/services/${s.enSlug}`)
      return en ? `/en/services/${s.enSlug}` : `/servicios/${s.esSlug}`;
  }
  for (const l of LOCATIONS) {
    if (pathname === `/ubicaciones/${l.esSlug}` || pathname === `/en/locations/${l.enSlug}`)
      return en ? `/en/locations/${l.enSlug}` : `/ubicaciones/${l.esSlug}`;
  }
  if (pathname === ABOUT.esPath || pathname === ABOUT.enPath) return en ? ABOUT.enPath : ABOUT.esPath;
  if (pathname === FAQ_PAGE.esPath || pathname === FAQ_PAGE.enPath) return en ? FAQ_PAGE.enPath : FAQ_PAGE.esPath;
  for (const base of SIMPLE_DUAL) {
    if (pathname === base || pathname === "/en" + base) return en ? "/en" + base : base;
  }
  return null;
}

export default function Nav() {
  const { lang, setLang, t, L } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [progress, setProgress] = useState(0);
  const [memberName, setMemberName] = useState("");
  const nav = useNavigate();
  const loc = useLocation();
  const lp = (path) => (lang === "en" ? "/en" + path : path);
  const isAutos = loc.pathname.startsWith("/autos") || loc.pathname.startsWith("/en/cars");
  const worldTo = isAutos ? (lang === "en" ? "/en/aviation" : "/aviacion") : (lang === "en" ? "/en/cars" : "/autos");
  const worldLabel = isAutos ? L("Aviation", "Aviación") : L("Automobiles", "Automóviles");
  const quoteCarTo = lang === "en" ? "/en/cars/quote" : "/autos/cotizar";

  useEffect(() => {
    const has = localStorage.getItem("tb_member");
    setMemberName(has ? (localStorage.getItem("tb_member_name") || "") : "");
  }, [loc.pathname]);

  useEffect(() => {
    let last = window.scrollY;
    const f = () => {
      const y = window.scrollY;
      setScrolled(y > 16);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(1, y / h) : 0);
      // auto-hide on scroll down, reveal on scroll up
      if (y > 120 && y > last + 4) setHidden(true);
      else if (y < last - 4) setHidden(false);
      last = y;
    };
    f();
    window.addEventListener("scroll", f, { passive: true });
    window.addEventListener("resize", f);
    return () => { window.removeEventListener("scroll", f); window.removeEventListener("resize", f); };
  }, []);

  const Brand = (props) => (
    <Link to={lang === "en" ? "/en" : "/"} data-testid="brand-logo" className="tb-brand flex items-center no-underline" {...props}>
      <svg viewBox="0 0 220 50" height="30" style={{ display: "block", width: "auto" }} fill="none" aria-label="DUKK">
        <g transform="translate(14 9) scale(0.2909)" stroke="#F5F2EE" strokeLinecap="round" fill="none">
          <path d="M5 86C35 72 75 44 118 10" strokeWidth="1.9" />
          <path d="M5 99C35 86 75 58 111 28" strokeWidth="1.9" />
          <path d="M118 10C116 17 113 23 111 28" strokeWidth="1.9" />
          <line x1="0" y1="104" x2="160" y2="104" strokeWidth="1.4" />
        </g>
        <text x="76" y="25" fontFamily="'Cormorant Garamond','Cormorant',Georgia,serif" fontWeight="300" fontSize="28" letterSpacing="8" fill="#F5F2EE">DUKK</text>
      </svg>
    </Link>
  );

  const switchLang = () => {
    const target = lang === "es" ? "en" : "es";
    const sib = siblingPath(loc.pathname, target);
    setLang(target);
    if (sib) nav(sib);
  };

  const LangBtn = () => (
    <button
      data-testid="lang-toggle"
      onClick={switchLang}
      className="font-mono"
      style={{ cursor: "pointer", border: "1px solid rgba(255,255,255,.16)", borderRadius: 4, padding: "5px 11px", fontSize: 11, letterSpacing: ".08em", color: "#F5F5F2", background: "transparent" }}
    >
      {lang === "es" ? "EN" : "ES"}
    </button>
  );

  const BookCta = (props) => (
    <button
      data-testid="nav-book-cta"
      onClick={() => nav(isAutos ? quoteCarTo : lp("/book"))}
      className="tb-cta"
      style={{ cursor: "pointer", background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 4, padding: "9px 18px", fontSize: 13, border: "none", whiteSpace: "nowrap" }}
      {...props}
    >
      {isAutos ? L("Get quote", "Cotizar") : L("Book", "Reservar")}
    </button>
  );

  const WorldSwitch = () => (
    <Link
      to={worldTo}
      data-testid="nav-world-switch"
      className="tbnav no-underline font-mono"
      style={{ color: "#C7A668", cursor: "pointer", border: "1px solid rgba(199,166,104,.4)", borderRadius: 999, padding: "6px 14px", fontSize: 11.5, letterSpacing: ".08em", whiteSpace: "nowrap" }}
    >
      {worldLabel} →
    </Link>
  );

  return (
    <nav
      className="tb-nav sticky top-0 z-50"
      style={{
        padding: "14px clamp(18px,5vw,64px)",
        background: scrolled ? "rgba(8,9,10,.62)" : "rgba(8,9,10,.34)",
        backdropFilter: "blur(22px) saturate(125%)",
        WebkitBackdropFilter: "blur(22px) saturate(125%)",
        borderBottom: "1px solid rgba(255,255,255,.08)",
        transition: "background .4s ease, transform .45s cubic-bezier(.16,.84,.44,1)",
        transform: hidden ? "translateY(-110%)" : "translateY(0)",
        position: "sticky",
      }}
    >
      <div className="tb-progress" style={{ transform: `scaleX(${progress})`, opacity: progress > 0.01 ? 1 : 0 }} />

      {/* Desktop layout */}
      <div className="hidden md:flex items-center justify-between">
        <Brand style={{ marginRight: 24 }} />
        <div className="tb-nav-links flex items-center" style={{ gap: "clamp(14px,2.4vw,32px)", fontSize: 13.5, color: "#B8BDC3" }}>
          {isAutos ? (
            <>
              <Link to={lang === "en" ? "/en/cars" : "/autos"} data-testid="nav-autos-services" className="tbnav no-underline" style={{ color: "#B8BDC3", cursor: "pointer" }}>{L("Services", "Servicios")}</Link>
              <Link to={lang === "en" ? "/en/cars/memberships" : "/autos/membresias"} data-testid="nav-autos-memberships" className="tbnav no-underline" style={{ color: "#B8BDC3", cursor: "pointer" }}>{L("Memberships", "Membresías")}</Link>
              <Link to={lang === "en" ? "/en/garage" : "/garaje"} data-testid="nav-autos-garage" className="tbnav no-underline" style={{ color: "#C7A668", cursor: "pointer" }}>{L("The Garage", "El Garaje")}</Link>
            </>
          ) : (
            <>
              <Link to={lp("/book")} data-testid="nav-book" className="tbnav no-underline" style={{ color: "#B8BDC3", cursor: "pointer" }}>{t.navServices}</Link>
              <Link to={lp("/membership")} data-testid="nav-membership" className="tbnav no-underline" style={{ color: "#B8BDC3", cursor: "pointer" }}>{t.navPlans}</Link>
              <Link to={lp("/track")} data-testid="nav-track" className="tbnav no-underline" style={{ color: "#C7A668", cursor: "pointer" }}>{t.navTrack}</Link>
            </>
          )}
          <WorldSwitch />
          <LangBtn />
          {!isAutos && (memberName ? (
            <Link to="/portal" data-testid="nav-member-account" className="tbnav no-underline" style={{ color: "#C7A668", cursor: "pointer", border: "1px solid rgba(199,166,104,.45)", borderRadius: 4, padding: "7px 14px", fontSize: 12.5 }}>{memberName.split(" ")[0]}</Link>
          ) : (
            <Link to="/portal" data-testid="nav-client-login" className="tbnav no-underline" style={{ color: "#B8BDC3", cursor: "pointer", border: "1px solid rgba(255,255,255,.16)", borderRadius: 4, padding: "7px 14px", fontSize: 12.5 }}>{L("Client Login", "Acceso Clientes")}</Link>
          ))}
          <BookCta />
        </div>
      </div>

      {/* Mobile: single row — DUKK left, world switch + CTA right */}
      <div className="flex md:hidden items-center justify-between w-full">
        <Brand />
        <div className="flex items-center" style={{ gap: 12 }}>
          <WorldSwitch />
          {!isAutos && <Link to={lp("/track")} data-testid="nav-track-mobile" className="tbnav no-underline" style={{ color: "#C7A668", cursor: "pointer", fontSize: 13.5, letterSpacing: ".02em" }}>{t.navTrack}</Link>}
          <BookCta />
        </div>
      </div>
    </nav>
  );
}
